import { PRODUCT } from "@/config/brand";
import { lazy, Suspense } from "react";
import HeroSection from "@/components/landing/HeroSection";
import SuiteOverview from "@/components/landing/SuiteOverview";
import TrustBand from "@/components/landing/TrustBand";
import { useSEO } from "@/hooks/useSEO";

// Defer below-the-fold sections.
const ConnectedLoop = lazy(() => import("@/components/landing/ConnectedLoop"));
const DemoVideoSection = lazy(() => import("@/components/landing/DemoVideoSection"));
const WorkflowSection = lazy(() => import("@/components/landing/WorkflowSection"));

const SectionFallback = () => <div className="min-h-[200px]" aria-hidden="true" />;

// FAQ content used both for the visible answers (if/when we render them)
// and for the FAQPage JSON-LD that Google ingests for rich results.
const faqs = [
  { q: "What is Pharmalify Spine?", a: "Pharmalify Spine is one connected GxP platform for biopharma quality, laboratory and asset operations. It brings eQMS, LIMS and CMMS workflows together with linked records, a full audit trail and single sign-on, with governed AI assistance and regulatory monitoring built in. Modules in scope depend on the licensed configuration." },
  { q: "Who is Pharmalify Spine built for?", a: "Emerging clinical biotechs through established mid-sized biopharmas, including hundreds of users across multi-department and multi-site teams." },
  { q: "Is Pharmalify Spine validated for GxP use?", a: "Spine is validation-ready and aligned with 21 CFR Part 11, EU GMP Annex 11, GAMP 5 and ALCOA+. It supports Part 11-style e-signatures, an append-only audit trail, and DB-enforced access and segregation of duties. A validation package and IQ/OQ/PQ support assist your team; formal validation is per customer intended use." },
  { q: "What can AI Agents do inside Spine?", a: "Governed, audit-trailed GxP assistants support document drafting, deviation triage, CAPA assistance and audit readiness within Spine’s compliance boundary. They use permitted context, propose and cite sources, and never decide or write records. Human review and approval remain required." },
  { q: "How does Regulatory Watch connect to Spine?", a: "Regulatory Watch is native regulatory intelligence inside Spine. Relevant guidance and enforcement insights feed Change Control, Quality Risk Management (QRM) and the SOP lifecycle for human impact assessment and controlled updates." },
  { q: "How do we get access to Pharmalify Spine?", a: "Access is contract-based only, after a formal company contract. Request a private walkthrough or biopharma consultation to assess scope, intended use, validation support and contract details." },
];

const Index = () => {
  useSEO({
    title: `${PRODUCT.name} | Unified GxP Operations for Biopharma`,
    description:
      "One connected GxP platform for biopharma quality, laboratory and asset operations: eQMS, LIMS and CMMS workflows with governed AI assistance and regulatory monitoring.",
    keywords:
      "Pharmalify Spine, connected GxP platform, biopharma eQMS LIMS CMMS, validation-ready, governed AI Agents, Regulatory Watch, 21 CFR Part 11",
    canonical: "https://www.pharmalify.com/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Pharmalify",
        url: "https://www.pharmalify.com/",

      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Pharmalify Spine and its native capabilities",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            item: {
              "@type": "SoftwareApplication",
              "@id": "https://www.pharmalify.com/#spine",
              name: PRODUCT.name,
              applicationCategory: "BusinessApplication",
              operatingSystem: "Web",
              url: "https://www.pharmalify.com/",
              description: "One connected GxP platform for biopharma quality, laboratory and asset operations, bringing eQMS, LIMS and CMMS workflows together. Contract-based access follows a formal company contract.",
              featureList: [
                "Quality (eQMS)", "QC Lab (LIMS)", "Asset Integrity (CMMS)",
                "AI Agents — native governed, audit-trailed GxP assistants inside Spine; propose and cite, never decide or write records",
                "Regulatory Watch — native regulatory intelligence feeding Spine’s Change Control, QRM and SOP lifecycle",
                "Validation package and IQ/OQ/PQ support; formal validation per customer intended use",
              ],
            },
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });
  return (
    <main className="min-h-screen bg-background">
      <HeroSection />
      <SuiteOverview />
      <TrustBand />
      <Suspense fallback={<SectionFallback />}>
        <ConnectedLoop />
        <DemoVideoSection />
        <WorkflowSection />
      </Suspense>
    </main>
  );
};

export default Index;
